import { useQuery } from "@tanstack/react-query";

import { Badge, Card, Eyebrow, H2, Stat, Text } from "@lagoon/ui";

import { api } from "../api.js";
import { planMeters, planName, trialLine } from "../plan.js";
import { Notice } from "./feedback.js";

/**
 * Where this organisation stands, on the screen everybody lands on (LGN-170).
 *
 * The plan, the trial and the three meters were previously visible only on the
 * billing screen, which `GET /api/billing` restricts to `owner` and `billing`.
 * So an admin whose next site was about to be refused, or a member whose trial
 * ended on Friday, had nowhere to read either, while `get_account` had been
 * telling a model both since LGN-167.
 *
 * **The figures come from the account route, not from a second count.**
 * `GET /api/orgs/:orgId/account` is `accountOrgForOwner` plus
 * `accountOrgSummary`, which is the pair behind `GET /internal/account` and
 * therefore behind `get_account`. A customer who reads "4 of 5 sites" here and
 * hears "3 of 5" from Claude has been told one of two lies and will plan
 * around whichever they saw last, so there is one arithmetic and this screen
 * does none of it.
 *
 * **Silent when it cannot load.** This is context beside a working screen and
 * not the screen: the sites list must not turn into a red box because a
 * secondary read failed. `retry: false` for the same reason, and the same
 * reason the shell's entitlements query uses it.
 */
export function OrgOverview(props: { orgId: string }): React.ReactElement | null {
  const account = useQuery({
    queryKey: ["org-account", props.orgId],
    queryFn: () => api.getOrgAccount(props.orgId),
    retry: false,
  });

  if (account.data === undefined) return null;
  const org = account.data;
  const trial = trialLine(org, new Date());

  return (
    <Card as="section" interactive={false}>
      <div className="lg:flex lg:flex-col lg:gap-5">
        <div className="lg:flex lg:flex-wrap lg:items-start lg:justify-between lg:gap-4">
          <div>
            <Eyebrow>Current plan</Eyebrow>
            <H2 className="lg:mt-2 lg:text-h3">{planName(org.plan)}</H2>
          </div>
          {org.suspended ? <Badge tone="danger">Suspended</Badge> : null}
        </div>

        {/*
          A live trial in its last three days gets a coloured box and nothing
          else does, which is `trialCountdown`'s threshold and the billing
          screen's rule. A trial is the state a new customer is meant to be in;
          a fortnight of warnings teaches people to ignore warnings.
        */}
        {trial === null ? null : trial.pressing ? (
          <Notice tone="warning">{trial.line}</Notice>
        ) : (
          <Text size="caption" tone="subtle">
            {trial.line}
          </Text>
        )}

        <div className="lg:grid lg:gap-4 lg:sm:grid-cols-3">
          {planMeters(org).map((meter) => (
            <Stat
              key={meter.label}
              value={meter.value}
              label={meter.label}
              {...(meter.foot === null ? {} : { foot: meter.foot })}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}
